import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useBranding } from '../../hooks/useBranding.js';
import { useCardSettings } from '../../hooks/useCardSettings.js';
import { useCardLayout } from '../../hooks/useCardLayout.js';
import { generateQR, generateCardPdf, verifyUrl } from '../../lib/cards';

/**
 * Staff card export panel.
 *
 * Renders inside the StaffEditor (Chunk 2B). Shows:
 *   - Small QR preview
 *   - The public verify URL
 *   - Buttons to download QR as PNG, or download full ID card as PDF
 *
 * Requires:
 *   - tenant.slug (from useAuth context)
 *   - staff.slug (from the staff record being edited)
 *   - branding (from useBranding)
 *   - card settings (from useCardSettings) — Session B
 *
 * Bundle C fix (Apr 23 2026): now pulls card settings via
 * useCardSettings() and merges them into the tenant object passed to
 * generateCardPdf. Without this, the saved template + knobs from the
 * ID Cards settings tab never reached the renderer, so downloads
 * silently fell back to the Minimal template.
 *
 * Also switched the import from '../../lib/card-export.js' to
 * '../../lib/cards' — both resolve to the same facade via the compat
 * shim, but using the new path keeps future grep-for-callers clean.
 *
 * Shows nothing if staff record doesn't exist yet (create mode).
 * Works entirely client-side — no new backend endpoints needed.
 */

export function StaffCardExport({ staff }) {
  const { tenant } = useAuth();
  const { branding } = useBranding();
  const { card } = useCardSettings();
  const { layout: cardLayout } = useCardLayout();

  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [qrError, setQrError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const url = tenant?.slug && staff?.slug ? verifyUrl(tenant.slug, staff.slug) : null;

  useEffect(() => {
    if (!url) return;
    (async () => {
      try {
        const data = await generateQR(url, 256);
        setQrDataUrl(data);
        setQrError(null);
      } catch (e) {
        setQrError(e?.message || 'Could not generate QR code');
      }
    })();
  }, [url]);

  if (!staff || !tenant) return null;

  async function handleDownloadQR() {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `tocynid-qr-${staff.slug}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  async function handleDownloadPdf() {
    setGenerating(true);
    setDownloadError(null);
    try {
      const pdf = await generateCardPdf({
        staff,
        tenant: {
          // Identity
          name: tenant.name,
          slug: tenant.slug,
          // Branding (unchanged)
          brand_color: branding?.brand_color,
          logo_url: branding?.logo_url,
          public_phone: branding?.public_phone,
          public_website: branding?.public_website,
          public_tagline: branding?.public_tagline,
          display_name_mode: branding?.display_name_mode || 'full',
          // Card settings (Session B fields — added in Bundle C)
          card_template: card?.template || 'minimal',
          card_company_name: card?.company_name || null,
          card_bottom_image_url: card?.bottom_image_url || null,
          card_photo_size: card?.photo_size || 'medium',
          card_name_font_size: card?.name_font_size || 'medium',
          card_footer_text_color: card?.footer_text_color || 'auto',
          card_banner_opacity: typeof card?.banner_opacity === 'number'
            ? card.banner_opacity
            : 100,
          card_layout_json: cardLayout || null,
        },
        staffSlug: staff.slug,
      });
      const filename = `tocynid-card-${staff.first_name}-${staff.last_name}-${staff.slug}.pdf`
        .toLowerCase()
        .replace(/[^a-z0-9.-]+/g, '-');
      pdf.save(filename);
    } catch (e) {
      console.error('[card-export] PDF generation failed:', e);
      setDownloadError(
        e?.message ||
        'Could not generate PDF. Check that the staff photo and logo load correctly.',
      );
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="qr-export-card">
      <div className="qr-preview">
        {qrDataUrl ? (
          <img src={qrDataUrl} alt={`QR code for ${staff.first_name} ${staff.last_name}`} />
        ) : qrError ? (
          <div className="muted" style={{ fontSize: 'var(--fs-xs)', padding: 'var(--sp-2)' }}>
            QR generation failed
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <span className="spinner" aria-label="Generating QR code" />
          </div>
        )}
      </div>

      <div className="qr-export-body">
        <h4 className="qr-export-title">Verify URL + QR</h4>

        {url && (
          <div className="qr-export-url" role="group" aria-label="Verify URL">
            {url}
          </div>
        )}

        {downloadError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-2)' }} role="alert">
            {downloadError}
          </div>
        )}

        <div className="qr-export-actions">
          <button
            type="button"
            className="btn btn-secondary"
            style={{ fontSize: 'var(--fs-sm)', padding: '0.45rem 0.9rem' }}
            onClick={handleDownloadQR}
            disabled={!qrDataUrl}
          >
            Download QR (PNG)
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ fontSize: 'var(--fs-sm)', padding: '0.45rem 0.9rem' }}
            onClick={handleDownloadPdf}
            disabled={generating || !qrDataUrl}
            aria-busy={generating}
          >
            {generating ? (
              <><span className="spinner" aria-hidden="true" /> Generating…</>
            ) : (
              'Download ID card (PDF)'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
